import { useState, useEffect, useRef } from 'react';
import ApiService from '../services/api';

export const PRACTICE_KEY_DELIMITER = '|||';

export const getDisplayNameFromKey = (key: string): string => {
  const delimIndex = key.lastIndexOf(PRACTICE_KEY_DELIMITER);
  return delimIndex >= 0 ? key.substring(0, delimIndex) : key;
};

export interface PracticeOption {
  label: string;
  value: string;
  isCorporate?: boolean;
  isChild?: boolean;
}

export interface PracticeData {
  /** Delimiter-keyed display strings (e.g. "Name|||acctNum") */
  allCustomers: string[];
  /** Map from delimiter key → account number */
  practiceMap: Record<string, string>;
  /** Set of delimiter keys that are corporate parents */
  corporateParentSet: Set<string>;
  /** Object-based options for PrimeReact Dropdown (label/value/flags) */
  practiceOptions: PracticeOption[];
  isLoading: boolean;
  isDemoMode: boolean;
  practiceAccountNumbers: string[];
  isInternal: boolean;
  orgDropdownLocked: boolean;
  /** A readable loading failure for chrome; never keep the app behind a spinner. */
  error: string | null;
  /** Signed-in user as resolved by the backend (first + last name, email). */
  userDisplayName: string;
  userEmail: string;
}

interface CachedPracticeData {
  allCustomers: string[];
  practiceMap: Record<string, string>;
  corporateParentSet: string[];
  practiceOptions: PracticeOption[];
  isDemoMode: boolean;
  practiceAccountNumbers: string[];
  isInternal: boolean;
  orgDropdownLocked: boolean;
  userDisplayName?: string;
  userEmail?: string;
}

export const PRACTICES_SESSION_KEY = 'practiceData';
export const CATEGORIES_SESSION_KEY = 'categoriesByAccount';
export const PRODUCT_FAMILIES_SESSION_KEY = 'productFamiliesByAccount';
export const EXCLUDED_OPPORTUNITIES_SESSION_KEY = 'excludedOpportunitiesByAccount';

const readCache = (): CachedPracticeData | null => {
  try {
    const raw = sessionStorage.getItem(PRACTICES_SESSION_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as CachedPracticeData;
  } catch {
    return null;
  }
};

const writeCache = (data: CachedPracticeData): void => {
  try {
    sessionStorage.setItem(PRACTICES_SESSION_KEY, JSON.stringify(data));
  } catch {
    // Storage quota exceeded or unavailable — silently skip
  }
};

export const usePracticeData = (): PracticeData => {
  const [allCustomers, setAllCustomers] = useState<string[]>([]);
  const [practiceMap, setPracticeMap] = useState<Record<string, string>>({});
  const [corporateParentSet, setCorporateParentSet] = useState<Set<string>>(new Set());
  const [practiceOptions, setPracticeOptions] = useState<PracticeOption[]>([]);
  // Start in the loading state when there's no cache, so consumers can show a
  // spinner immediately on first render (no flash) until the fetch resolves.
  const [isLoading, setIsLoading] = useState<boolean>(() => readCache() === null);
  const [isDemoMode, setIsDemoMode] = useState(false);
  const [practiceAccountNumbers, setPracticeAccountNumbers] = useState<string[]>([]);
  const [isInternal, setIsInternal] = useState(false);
  const [orgDropdownLocked, setOrgDropdownLocked] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [userDisplayName, setUserDisplayName] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const hasLoadedRef = useRef(false);

  useEffect(() => {
    if (hasLoadedRef.current) return;
    hasLoadedRef.current = true;

    const cached = readCache();
    if (cached) {
      setAllCustomers(cached.allCustomers);
      setPracticeMap(cached.practiceMap);
      setCorporateParentSet(new Set(cached.corporateParentSet));
      setPracticeOptions(cached.practiceOptions);
      setIsDemoMode(cached.isDemoMode ?? false);
      setPracticeAccountNumbers(cached.practiceAccountNumbers ?? []);
      setIsInternal(cached.isInternal ?? false);
      setOrgDropdownLocked(cached.orgDropdownLocked ?? false);
      setUserDisplayName(cached.userDisplayName ?? '');
      setUserEmail(cached.userEmail ?? '');
      return;
    }

    const loadPractices = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await ApiService.getUserPractices();
        if (!response.data?.practice_info) {
          setAllCustomers([]);
          setPracticeMap({});
          setCorporateParentSet(new Set());
          setPracticeOptions([]);
          return;
        }

        const displayName = [response.data.first_name, response.data.last_name].filter(Boolean).join(' ').trim();
        const email = response.data.email ?? '';
        setUserDisplayName(displayName);
        setUserEmail(email);

        const demoMode = Array.isArray(response.data?.extended_permissions) &&
          response.data.extended_permissions.some(p => p.name === 'ScenarioIQ Demo Mode');
        setIsDemoMode(demoMode);
        setPracticeAccountNumbers(response.data?.practice_account_numbers || []);

        const displayNames: string[] = [];
        const map: Record<string, string> = {};
        const corporateNames = new Set<string>();
        const options: PracticeOption[] = [];
        const addedAccountNumbers = new Set<string>();

        const corporatePracticeMapping = (response.data as any).corporate_practice_mapping;

        if (corporatePracticeMapping && Array.isArray(corporatePracticeMapping) && corporatePracticeMapping.length > 0) {
          const sortedMapping = [...corporatePracticeMapping].sort((a: any, b: any) => {
            const nameA = (a.displayName || a.name || a.accountNumber).toLowerCase();
            const nameB = (b.displayName || b.name || b.accountNumber).toLowerCase();
            return nameA.localeCompare(nameB);
          });

          sortedMapping.forEach((corpEntity: any) => {
            if (addedAccountNumbers.has(corpEntity.accountNumber)) return;

            const corpDisplay = corpEntity.displayName || corpEntity.name || corpEntity.accountNumber;
            const corpKey = `${corpDisplay}${PRACTICE_KEY_DELIMITER}${corpEntity.accountNumber}`;
            displayNames.push(corpKey);
            map[corpKey] = corpEntity.accountNumber;
            corporateNames.add(corpKey);
            options.push({ label: corpDisplay, value: corpEntity.accountNumber, isCorporate: true });
            addedAccountNumbers.add(corpEntity.accountNumber);

            if (corpEntity.practices && Array.isArray(corpEntity.practices)) {
              corpEntity.practices.forEach((practice: any) => {
                if (addedAccountNumbers.has(practice.accountNumber)) return;
                const name = practice.displayName || practice.name || 'Unknown';
                const displayString = `    ${name}`;
                const uniqueKey = `${displayString}${PRACTICE_KEY_DELIMITER}${practice.accountNumber}`;
                displayNames.push(uniqueKey);
                map[uniqueKey] = practice.accountNumber;
                options.push({ label: name, value: practice.accountNumber, isChild: true });
                addedAccountNumbers.add(practice.accountNumber);
              });
            }
          });
        } else {
          const hasCorporateParent = (response.data as any).corporate_name &&
            (response.data as any).corporate_account_number &&
            (response.data as any).corporate_name.trim() !== '' &&
            (response.data as any).corporate_account_number.trim() !== '';

          if (hasCorporateParent) {
            const corpDisplay = (response.data as any).corporate_name;
            const corpAcct = (response.data as any).corporate_account_number;
            const corpKey = `${corpDisplay}${PRACTICE_KEY_DELIMITER}${corpAcct}`;
            displayNames.push(corpKey);
            map[corpKey] = corpAcct;
            corporateNames.add(corpKey);
            options.push({ label: corpDisplay, value: corpAcct, isCorporate: true });
            addedAccountNumbers.add(corpAcct);

            response.data.practice_info.forEach((practice) => {
              if (addedAccountNumbers.has(practice.accountNumber)) return;
              const name = practice.displayName || practice.name || 'Unknown';
              const displayString = `    ${name}`;
              const uniqueKey = `${displayString}${PRACTICE_KEY_DELIMITER}${practice.accountNumber}`;
              displayNames.push(uniqueKey);
              map[uniqueKey] = practice.accountNumber;
              options.push({ label: name, value: practice.accountNumber, isChild: true });
              addedAccountNumbers.add(practice.accountNumber);
            });
          } else {
            response.data.practice_info.forEach((practice) => {
              if (addedAccountNumbers.has(practice.accountNumber)) return;
              const name = practice.displayName || practice.name || 'Unknown';
              const uniqueKey = `${name}${PRACTICE_KEY_DELIMITER}${practice.accountNumber}`;
              displayNames.push(uniqueKey);
              map[uniqueKey] = practice.accountNumber;
              options.push({ label: name, value: practice.accountNumber });
              addedAccountNumbers.add(practice.accountNumber);
            });
          }
        }

        const internal = response.data.is_ally_only ?? false;

        let locked = false;
        if (!internal) {
          if (corporatePracticeMapping && Array.isArray(corporatePracticeMapping) && corporatePracticeMapping.length > 0) {
            locked = corporatePracticeMapping.length === 1 &&
              (corporatePracticeMapping[0].practices?.length ?? 0) <= 1;
          } else {
            const corpName = (response.data as any).corporate_name;
            const corpAcct = (response.data as any).corporate_account_number;
            const hasCorp = corpName?.trim() && corpAcct?.trim();
            if (hasCorp) {
              locked = response.data.practice_info.length <= 1;
            } else {
              locked = true;
            }
          }
        }

        setAllCustomers(displayNames);
        setPracticeMap(map);
        setCorporateParentSet(corporateNames);
        setPracticeOptions(options);
        setIsInternal(internal);
        setOrgDropdownLocked(locked);

        writeCache({
          allCustomers: displayNames,
          practiceMap: map,
          corporateParentSet: Array.from(corporateNames),
          practiceOptions: options,
          isDemoMode: demoMode,
          practiceAccountNumbers: response.data?.practice_account_numbers || [],
          isInternal: internal,
          orgDropdownLocked: locked,
          userDisplayName: displayName,
          userEmail: email,
        });
      } catch (error) {
        console.error('Failed to load practices:', error);
        const message = error instanceof Error ? error.message : 'The practices service did not return a response.';
        setError(message);
      } finally {
        setIsLoading(false);
      }
    };

    loadPractices();
  }, []);

  return { allCustomers, practiceMap, corporateParentSet, practiceOptions, isLoading, isDemoMode, practiceAccountNumbers, isInternal, orgDropdownLocked, error, userDisplayName, userEmail };
};
